import React from 'react';
import { useHistory } from 'react-router-dom';
import { Icon, Title, Space, Button } from 'components';
import {
  Button as ElementsButton,
  Icon as ElementsIcon,
  Flex,
} from '@bb/elements-web';
import { featureFlagClient } from 'utils';
import styled from 'styled-components';
import { useBreakpoint } from 'hooks';
import ShowingDropdown from './ShowingsDropdown';
import { useShowingsCalendarContext } from '../ShowingsCalendarContext';
import { useManagedAccessDrawerContext } from '../ManagedAccessDrawer/ManagedAccessDrawerContext';
import ListingImage from '../Tables/components/ListingImage';

const ShowingHeader: React.FC = () => {
  const history = useHistory();
  const { md } = useBreakpoint();
  const context = useShowingsCalendarContext();
  const { managedAccessDrawerOpen, setManagedAccessDrawerOpen } =
    useManagedAccessDrawerContext();
  const {
    calendarType,
    listing,
    listingId,
    managedAccessCount,
    managedAccessLoading,
  } = context;

  const isListingCalendar = calendarType === 'listing';
  const showExtraHeader = isListingCalendar && !md;
  const headerTitle = isListingCalendar ? (
    <Space size={12}>
      <button
        onClick={history.goBack}
        style={{ display: 'flex', alignItems: 'center' }}
      >
        <ElementsIcon icon="arrowLeftOutlined" color="black" size={18} />
      </button>
      {md && <ListingImage thumbnail={listing.thumbnail} />}
      <StyledTitle level={md ? 1 : 3}>
        {listing.line1} {listing.line2 || ''}
      </StyledTitle>
    </Space>
  ) : (
    <Title level={1} hideMargin data-cy="showings-calendar-title">
      Showings
    </Title>
  );

  const headerExtra = (
    <div>
      <ShowingDropdown />
      <Button
        ghost
        icon={<Icon type="settings" color="black" />}
        href={`#/listing/${listingId}/appointments/config`}
        data-cy="showing-config-button"
      />
    </div>
  );

  const isSingleAccessEnabled = featureFlagClient.featureEnabled(
    'supraSingleAccessFeature',
    1,
  );

  // determine the correct label for managed access count (singular vs plural)
  const managedAccessLabel =
    managedAccessCount === 1
      ? `managed access appointment`
      : 'managed access appointments';

  return (
    <StyledPageHeader>
      <Flex
        vertical={showExtraHeader} // display in a column on listing calendar for small screens, row otherwise
        align="center"
        justify="space-between"
        flex={1}
        gap={12}
      >
        <Flex align="center" justify="space-between" gap={12}>
          <div>
            <Space wrap>{headerTitle}</Space>
          </div>
          {showExtraHeader && headerExtra}
        </Flex>
        {isSingleAccessEnabled && managedAccessCount > 0 && (
          <ElementsButton
            type="secondary"
            size="middle"
            onClick={() => setManagedAccessDrawerOpen(!managedAccessDrawerOpen)}
            dataCy="managed-access-drawer-button"
            loading={managedAccessLoading}
          >
            {managedAccessCount} {managedAccessLabel}
            <ElementsIcon icon="arrowRightOutlined" size={14} color="primary" />
          </ElementsButton>
        )}
      </Flex>
    </StyledPageHeader>
  );
};

const StyledTitle = styled(Title)`
  margin: 0 !important;
`;

const StyledPageHeader = styled.div`
  width: 100%;
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin: 0;
  padding: 0;
  h1 {
    display: inline;
  }
`;

export default ShowingHeader;
